import { Button, Flex, Stack, Tabs, Text } from '@mantine/core';
import pluralize from 'pluralize';
import { useCallback, useMemo, useState } from 'react';
import { NavLink } from 'react-router';
import { FileUploadInput, UploadedFile } from 'src/data-capture/hooks/file-uploads/types';
import { useFileUploadActions } from 'src/data-capture/hooks/file-uploads/useFileUploadActions';
import { useFileUploads } from 'src/data-capture/hooks/file-uploads/useFileUploads';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { StepProps } from 'src/UI/drawer-flow/DrawerFlow';
import DrawerFlowScreenLayout from 'src/UI/drawer-flow/DrawerFlowScreenLayout';
import SelectableItemList from 'src/UI/drawer-flow/SelectableItemList';
import { notifyAndLogError } from 'src/UI/common/ui-helpers';
import { FONT_WEIGHTS } from 'src/UI/theme/theme';
import { Client } from 'src/users';
import { useMeetingNote } from '../MeetingNoteProvider';
import { MeetingNoteData, MeetingNoteSourceType } from '../MeetingNoteFlow';

const AUDIO_MIME_TYPES = [
  'audio/mpeg',
  'audio/wav',
  'audio/x-wav',
  'audio/mp4',
  'audio/x-m4a',
  'video/mp4',
  'audio/aac',
  'audio/flac',
  'audio/ogg',
  'audio/webm',
];

const DOCUMENT_MIME_TYPES = [
  'application/pdf',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/markdown',
];

const DOCUMENT_FILE_EXTENSIONS = ['.pdf', '.doc', '.docx', '.txt', '.md'];

const filterAudioFiles = (files: UploadedFile[]): UploadedFile[] => {
  return files.filter(file => AUDIO_MIME_TYPES.includes(file.file.fileType));
};

const filterDocumentFiles = (files: UploadedFile[]): UploadedFile[] => {
  return files.filter(file => DOCUMENT_MIME_TYPES.includes(file.file.fileType));
};

type SourceTab = 'recordings' | 'documents';

export default function SelectSourceStep(props: StepProps<MeetingNoteData>) {
  const { data, setData } = props;
  const { flagEnabledMeetingNoteFromDocument } = useFlags();
  const { files, isLoading } = useFileUploads({
    clientId: data.client?.id,
  });
  const { uploadFiles } = useFileUploadActions({ clientId: data.client?.id });
  const [isUploading, setIsUploading] = useState(false);

  const audioItems = useMemo(() => {
    return filterAudioFiles(files).map(file => ({
      id: file.id,
      title: file.file.fileName,
      description: `Uploaded: ${new Date(file.file.createdAt).toLocaleDateString()}`,
    }));
  }, [files]);

  const documentItems = useMemo(() => {
    return filterDocumentFiles(files).map(file => ({
      id: file.id,
      title: file.file.fileName,
      description: `Uploaded: ${new Date(file.file.createdAt).toLocaleDateString()}`,
    }));
  }, [files]);

  const defaultTab: SourceTab =
    audioItems.length === 0 && documentItems.length > 0 ? 'documents' : 'recordings';
  const [activeTab, setActiveTab] = useState<SourceTab>(defaultTab);

  const handleTabChange = useCallback(
    (value: string | null) => {
      const tab = (value ?? 'recordings') as SourceTab;
      setActiveTab(tab);
      setData({ ...data, selectedRecordingId: undefined, selectedSourceType: undefined });
    },
    [data, setData],
  );

  const handleSelection = useCallback(
    (selectedIds: string[], sourceType: MeetingNoteSourceType) => {
      setData({
        ...data,
        selectedRecordingId: selectedIds[0] || undefined,
        selectedSourceType: selectedIds[0] ? sourceType : undefined,
      });
    },
    [data, setData],
  );

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const fileList = event.target.files;

    if (fileList) {
      setIsUploading(true);
      try {
        const payload: FileUploadInput[] = Array.from(fileList).map(file => ({
          file,
          isResolved: true,
        }));
        const result = await uploadFiles(payload);
        const newUploadedFileId = result[0]?.data?.id;
        if (newUploadedFileId) {
          setData({
            ...data,
            selectedRecordingId: newUploadedFileId,
            selectedSourceType: 'document',
          });
        }
      } catch (error) {
        notifyAndLogError(
          'Failed to upload file',
          'An error occurred while uploading the document. Please try again.',
          error,
        );
      }
      setIsUploading(false);
    }

    event.target.value = '';
  };

  if (!flagEnabledMeetingNoteFromDocument) {
    return (
      <AudioOnlyView
        {...props}
        audioItems={audioItems}
        isLoading={isLoading}
        onSelectionChange={selectedIds => handleSelection(selectedIds, 'audio')}
      />
    );
  }

  return (
    <DrawerFlowScreenLayout
      {...props}
      title="Select source"
      description="Select a recording or transcript document to generate the meeting note from"
      isLoading={isLoading}
      canGoForward={props.canGoForward && !!data.selectedRecordingId}
    >
      <Tabs
        value={activeTab}
        onChange={handleTabChange}
        flex={1}
        mih={0}
        display="flex"
        style={{ flexDirection: 'column' }}
      >
        <Tabs.List style={{ flexShrink: 0 }}>
          <Tabs.Tab value="recordings">Recordings ({audioItems.length})</Tabs.Tab>
          <Tabs.Tab value="documents">Documents ({documentItems.length})</Tabs.Tab>
        </Tabs.List>

        <Tabs.Panel value="recordings" pt="sm" flex={1} mih={0}>
          <Flex direction="column" gap="xs" mih={0} flex={1} h="100%">
            <Text
              size="textSm"
              fw={FONT_WEIGHTS.medium}
              c="black"
              data-testid="recording-count"
              style={{ flexShrink: 0 }}
            >
              {audioItems.length} {pluralize('recording', audioItems.length)} found
            </Text>
            <SelectableItemList
              items={audioItems}
              hint="The information already uploaded to the client's profile, financial and cashflows areas will be added as supplementary information in the meeting note."
              selectedItemIds={
                data.selectedSourceType === 'audio' && data.selectedRecordingId
                  ? [data.selectedRecordingId]
                  : []
              }
              setSelectedItemIds={selectedIds => handleSelection(selectedIds, 'audio')}
              selectMultiple={false}
              emptyView={
                <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.10">
                  Upload a meeting recording (MP3, MP4, M4A, or WAV) to{' '}
                  <DocumentLink client={props.data.client} /> and it will appear here.
                </Text>
              }
            />
          </Flex>
        </Tabs.Panel>

        <Tabs.Panel value="documents" pt="sm" flex={1} mih={0}>
          <Flex direction="column" gap="xs" mih={0} flex={1} h="100%">
            <Flex justify="space-between" align="center" style={{ flexShrink: 0 }}>
              <Text size="textSm" fw={FONT_WEIGHTS.medium} c="black" data-testid="document-count">
                {documentItems.length} {pluralize('document', documentItems.length)} found
              </Text>
              <Button
                component="label"
                variant="subtle"
                size="compact-sm"
                loading={isUploading}
                style={{ cursor: 'pointer' }}
              >
                Upload document
                <input
                  type="file"
                  accept={DOCUMENT_FILE_EXTENSIONS.join(',')}
                  style={{ display: 'none' }}
                  onChange={handleFileUpload}
                />
              </Button>
            </Flex>
            <SelectableItemList
              items={documentItems}
              hint="The document content will be used as the transcript for meeting note generation."
              selectedItemIds={
                data.selectedSourceType === 'document' && data.selectedRecordingId
                  ? [data.selectedRecordingId]
                  : []
              }
              setSelectedItemIds={selectedIds => handleSelection(selectedIds, 'document')}
              selectMultiple={false}
              emptyView={
                <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.10">
                  Upload a transcript document (PDF, Word, or text file) using the button above or
                  to <DocumentLink client={props.data.client} /> and it will appear here.
                </Text>
              }
            />
          </Flex>
        </Tabs.Panel>
      </Tabs>
    </DrawerFlowScreenLayout>
  );
}

function AudioOnlyView(
  props: StepProps<MeetingNoteData> & {
    audioItems: { id: string; title: string; description: string }[];
    isLoading: boolean;
    onSelectionChange: (selectedIds: string[]) => void;
  },
) {
  const { data, audioItems, isLoading, onSelectionChange } = props;

  return (
    <DrawerFlowScreenLayout
      {...props}
      title="Select recording"
      description="This recording will be used to generate the meeting note"
      isLoading={isLoading}
      canGoForward={props.canGoForward && !!data.selectedRecordingId}
      footer={
        audioItems.length > 0 && (
          <Stack gap="xxs">
            <Text size="textSm" fw={FONT_WEIGHTS.semibold} c="gray.10">
              Can&apos;t find the recording?
            </Text>
            <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.7">
              Upload the recording to <DocumentLink client={props.data.client} /> and it will appear
              here.
            </Text>
          </Stack>
        )
      }
    >
      <Flex direction="column" gap="xs" mih={0} flex={1} h="100%">
        <Text
          size="textSm"
          fw={FONT_WEIGHTS.medium}
          c="black"
          data-testid="recording-count"
          style={{ flexShrink: 0 }}
        >
          {audioItems.length} {pluralize('recording', audioItems.length)} found
        </Text>
        <SelectableItemList
          items={audioItems}
          hint="The information already uploaded to the client's profile, financial and cashflows areas will be added as supplementary information in the meeting note."
          selectedItemIds={data.selectedRecordingId ? [data.selectedRecordingId] : []}
          setSelectedItemIds={onSelectionChange}
          selectMultiple={false}
          emptyView={
            <Text size="textXs" fw={FONT_WEIGHTS.medium} c="gray.10">
              Upload a meeting recording (MP3, MP4, M4A, or WAV) to{' '}
              <DocumentLink client={props.data.client} /> and it will appear here.
            </Text>
          }
        />
      </Flex>
    </DrawerFlowScreenLayout>
  );
}

function DocumentLink({ client }: { client: Client | undefined }) {
  const { close } = useMeetingNote();

  if (client == null) {
    return null;
  }

  return (
    <Text
      component={NavLink}
      to={`/client/${client.id}/document/add`}
      size="textXs"
      fw={FONT_WEIGHTS.medium}
      c="brand.8"
      onClick={close}
    >
      {client?.firstName}&apos;s documents
    </Text>
  );
}
